import { loadWebComponents } from 'Utils/LoadResources'
import { getRandomIdentifier, isAlgoliaPage } from 'Utils/Utility'
import HoverIntent from 'Modules/HoverIntent'

export default class MenuManager {
	static get IDLE_CALLBACK_TIMEOUT() {
		return 3000
	}

	constructor() {
		if (document.body.classList.contains('header-minimal')) {
			return
		}

		this.onLevel0HoverIntent = this.onLevel0HoverIntent.bind(this)
		this.onLevel0ButtonClick = this.onLevel0ButtonClick.bind(this)
		this.onMobileTriggerClick = this.onMobileTriggerClick.bind(this)
		this.fetchMenu = this.fetchMenu.bind(this)
		this.onAlgoliaUserAction = this.onAlgoliaUserAction.bind(this)

		this.isAlgoliaPage = isAlgoliaPage()
		this.header = document.querySelector('.header-element')

		if (!this.header) {
			return
		}

		this.mobileTrigger = this.header.querySelector('.header-icons__item--menu button')
		this.mainMenu = this.header.querySelector('.menu-element')
		this.level0 = this.mainMenu.querySelector('.level-0')
		this.subMenuWrapper = this.mainMenu.querySelector('.js-layer')
		this.main = document.querySelector('main')
		this.algoliaTriggerDesktop = this.mainMenu.querySelector('.js-toggle-text-search')
		this.algoliaTriggerMobile = this.header.querySelector('.js-toggle-text-search-mobile')

		this.actionElement = null
		this.menuFetchPromise = null
		this.forcedMenuFetch = false
		this.desktopMenuHoverIntent = null
		this.menuRand = null

		loadWebComponents(['car-dialog', 'algolia-layer'])

		const hoveredButton = this.getHoveredButton()
		if (hoveredButton) {
			this.actionElement = hoveredButton
			this.onUserAction()
		} else {
			this.addMenuEventListeners()
		}
		this.otherBreakpointWatch(this.isMobile())

		this.addAlgoliaEventListeners()
	}

	getHoveredButton() {
		return this.level0.querySelector('button:hover')
	}

	addAlgoliaEventListeners() {
		this.algoliaTriggerDesktop.addEventListener('click', this.onAlgoliaUserAction)
		this.algoliaTriggerMobile.addEventListener('click', this.onAlgoliaUserAction)
	}

	removeAlgoliaEventListeners() {
		this.algoliaTriggerMobile.removeEventListener('click', this.onAlgoliaUserAction)
	}

	onAlgoliaUserAction() {
		if (this.isAlgoliaPage) {
			return
		}

		const element = document.createDocumentFragment()
		const wrapper = document.createElement('div')
		wrapper.setAttribute('id', 'dialog-instant-search')
		wrapper.innerHTML = `<car-dialog class="car-dialog" data-append-to-body="false" data-prevent-focus-back data-scope="instant-search" data-has-dialog-children="true">
                                <template>
                                    <algolia-layer
                                    class="algolia-layer"
                                    data-algolia-querystring="">
                                    </algolia-layer>
                                </template>
                            </car-dialog>`
		element.appendChild(wrapper)
		this.header.appendChild(element)
	}

	addMenuEventListeners() {
		requestIdleCallback(
			() => {
				if (!this.forcedMenuFetch) {
					this.fetchMenu()
				}
			},
			{
				timeout: MenuManager.IDLE_CALLBACK_TIMEOUT,
			}
		)
		this.desktopMenuHoverIntent = new HoverIntent(
			this.level0,
			// eslint-disable-next-line func-names
			() => this.onLevel0HoverIntent(),
			() => {} // to prevent "undefined onOut" console error spam
		)
		this.level0.addEventListener('click', this.onLevel0ButtonClick)
		this.mobileTrigger.addEventListener('click', this.onMobileTriggerClick)
	}

	removeMenuEventListeners() {
		this.desktopMenuHoverIntent?.remove()
		this.desktopMenuHoverIntent = null
		this.level0.removeEventListener('click', this.onLevel0ButtonClick)
		this.mobileTrigger.removeEventListener('click', this.onMobileTriggerClick)
	}

	resetStatus() {
		this.menuFetchPromise = null
		this.forcedMenuFetch = false
		this.menuRand = null

		this.removeMenuEventListeners()
	}

	onLevel0HoverIntent() {
		this.actionElement = this.getHoveredButton()
		this.onUserAction()
	}

	onLevel0ButtonClick(evt) {
		let desktopMenuClickedButton =
			evt.target instanceof HTMLButtonElement ? evt.target : evt.target.closest('button')
		desktopMenuClickedButton = desktopMenuClickedButton ?? evt.querySelector('button')

		this.actionElement = desktopMenuClickedButton
		this.onUserAction()
	}

	onMobileTriggerClick() {
		this.actionElement = this.mobileTrigger
		this.onUserAction()
	}

	onUserAction() {
		if (!this.menuFetchPromise) {
			this.fetchMenu()
			this.forcedMenuFetch = true
		}

		const currentMenuFetchPromise = this.menuFetchPromise
		const currentActionElement = this.actionElement
		const rand = getRandomIdentifier()
		this.menuRand = rand
		this.menuFetchPromise.then(() => {
			if (
				currentMenuFetchPromise !== this.menuFetchPromise ||
				currentActionElement !== this.actionElement ||
				rand !== this.menuRand
			) {
				return
			}

			if (this.actionElement === this.mobileTrigger) {
				yTos.notify('CAR::MenuButton::Clicked')
			} else if (this.actionElement instanceof HTMLButtonElement) {
				yTos.notify('CAR::ExpandMenuTab', this.actionElement)
			}
		})
	}

	fetchMenu() {
		if (this.menuFetchPromise || this.forcedMenuFetch) {
			return
		}

		this.menuFetchPromise = this.currentBreakpoint(this.isMobile())
		const currentMenuFetchPromise = this.menuFetchPromise
		this.menuFetchPromise.then(() => {
			if (currentMenuFetchPromise !== this.menuFetchPromise) {
				return
			}

			this.removeMenuEventListeners()
		})
	}

	currentBreakpoint(isMobile) {
		return new Promise((resolve) => {
			if (isMobile) {
				this.mobileMenu().then(() => {
					yTos.watchOne({ 'CAR::SidenavReady': resolve })
					loadWebComponents(['sidenav-element'])
				})
			} else {
				this.desktopMenu().then(() => {
					yTos.watchOne({ 'CAR::SubmenuReady': resolve })
					loadWebComponents(['submenu-element'])
				})
			}
		})
	}

	otherBreakpointWatch(isMobile) {
		yTos.watchOne(
			{
				'CAR::mediaChanged::breakpoint': () => {
					this.resetStatus()
					this.addMenuEventListeners()
				},
			},
			{ scope: isMobile ? 'desktop-medium' : 'desktop-upper' }
		)
	}

	isMobile() {
		return yTos.Cartier.devices.media['desktop-upper'].matches
	}

	async mobileMenu() {
		// Do not remove the commented bits, these may come in handy later on...
		// const currentUri = encodeURIComponent(
		// 	`${window.location.origin}${yTos.navigation.PathAndQuery.replaceAll('&amp;', '&')}`
		// )

		const options = {
			data: {
				device: 'mobile',
				// currentUri,
				// currentController: yTos.navigation.Controller,
			},
		}
		const response = await yTos.fetch('/Navigation/CustomizableSubMenuAsync', options)
		const data = await response.text()
		if (!this.subMenuWrapper.querySelector('.submenu-element')) {
			this.subMenuWrapper.textContent = ''
		}
		this.header.insertAdjacentHTML('afterend', data)
	}

	async desktopMenu() {
		// Do not remove the commented bits, these may come in handy later on...
		// const currentUri = encodeURIComponent(
		// 	`${window.location.origin}${yTos.navigation.PathAndQuery.replaceAll('&amp;', '&')}`
		// )

		const options = {
			data: {
				device: 'desktop',
				// currentUri,
				// currentController: yTos.navigation.Controller,
			},
		}
		const response = await yTos.fetch('/Navigation/CustomizableSubMenuAsync', options)
		const data = await response.text()
		this.subMenuWrapper.textContent = ''
		this.subMenuWrapper.insertAdjacentHTML('afterbegin', data)
	}
}
